import type {Ref} from 'vue';
import {createContext} from '@/shared/createContext';
import type {MixerTrack} from '@/composables/osmd/useOsmdPlayback';

/**
 * Kontext des Audio-Players einer Notenvorschau. `useOsmdInteractiveScore` stellt ihn bereit,
 * `OsmdPlaybackControls` (Transport/Tempo/Metronom/Mixer) und `PlaybackControlsOverlay` (nur `isPlaying`)
 * injizieren ihn.
 *
 * **Nur auf der EINGABE-Vorschau vorhanden (kritisch – nicht auf die Ausgabe ausweiten).** Die
 * Griffschrift ist keine spielbare Partitur (Knopf-Notation, Balgrichtung, Bass-Lyrics, rote
 * Pausen für unspielbare Töne) – Wiedergabe klänge dort zwangsläufig falsch. Der Provider hängt
 * deshalb an `UseOsmdInteractiveScoreOptions.enablePlayback` (Prop `enablePlayback` von
 * `MusicXmlPreview`, gesetzt nur von `PreviewPane` an der Eingabe-Vorschau). Ohne das Flag
 * entsteht kein `PlaybackManager`, es werden keine SoundFonts geladen, und dieser Kontext wird
 * gar nicht bereitgestellt. Injektion deshalb dort, wo die Vorschau beides bedienen muss, mit
 * `null`-Fallback (`injectScorePlayback(null)`) – analog {@link ./toolbarExtensionsContext}.
 *
 * **Pro Vorschau-Instanz, NIE ein Pinia-Singleton (nicht darauf umbauen).** Der `PlaybackManager`
 * gehört einer konkreten OSMD-Instanz und wird im `onBeforeUnmount` entsorgt; die Vorschau wird
 * bei jedem Tab-Wechsel/Upload/Konvertieren neu gemountet (siehe `osmd/playbackEngine.ts`). Ein
 * Store überlebte das und hielte `isPlaying`/`tracks`/BPM eines zerstörten Managers. Was die
 * Komponente WIRKLICH überlebt – SoundFont-Cache, der eine `AudioContext`, der Cursor-Taktindex –
 * liegt bereits korrekt in `stores/playbackStore.ts` + `getSharedAudioPlayer()`. Und ein Singleton
 * könnte „diese Vorschau hat keinen Player" gar nicht ausdrücken; die natürliche Kodierung dafür
 * ist „kein Provider".
 *
 * **Kein Lifecycle hier (kritisch).** `createPlayback`/`bindSheet`/`afterRender`/`disposePlayer`/
 * `setPlaybackStartToSelection` bleiben beim Orchestrator – ein Kind darf den Player DIESER
 * Vorschau weder initialisieren noch entsorgen. Der Typ ist genau die Rückgabe von
 * `useOsmdPlayback` MINUS dieser fünf Member; der Orchestrator liefert ihn per
 * Rest-Destrukturierung, damit die Liste nicht von Hand nachgepflegt werden muss.
 *
 * **Wann ein eigener Kontext statt `ScoreEditContext`?** Wenn (a) der Bereich nur auf einer der
 * beiden Vorschauen existiert (dann optionaler Kontext mit `null`-Fallback), ODER (b) ein
 * Konsumenten-Teilbaum (fast) nichts anderes aus `ScoreEditContext` nutzt – der Schnitt also
 * Injects ENTFERNT statt welche hinzuzufügen (`OsmdPlaybackControls`: 16/16 Player, 0 Edit).
 * Müsste irgendein Konsument zwei Injects machen, um EINE Aufgabe zu erledigen, bleiben die
 * Member im `ScoreEditContext`.
 */
export interface ScorePlaybackContext {
    // ---- Player-State ----
    isPlayerReady: Ref<boolean>;
    isPlaying: Ref<boolean>;
    /** SoundFonts des aktuellen Sheets geladen (geteilte Engine, siehe `osmd/playbackEngine.ts`). */
    isSoundReady: Ref<boolean>;
    /** SoundFonts werden gerade (einmalig pro App-Lebensdauer) geladen → Play zeigt Spinner. */
    isLoadingSounds: Ref<boolean>;
    /** Fortschrittstext für den Ladezustand („SoundFonts werden geladen … (2/4)"). */
    soundLoadLabel: Ref<string>;
    currentBpm: Ref<number>;
    metronomeEnabled: Ref<boolean>;
    tracks: Ref<MixerTrack[]>;

    // ---- Transport ----
    play: () => Promise<void>;
    pause: () => Promise<void>;
    stop: () => Promise<void>;

    // ---- Tempo + Mixer ----
    setBpm: (bpm: number) => void;
    setMetronome: (enabled: boolean) => void;
    setTrackVolume: (id: number, volume: number) => void;
    setTrackMuted: (id: number, muted: boolean) => void;
    setTrackSolo: (id: number, solo: boolean) => void;
}

export const [injectScorePlayback, provideScorePlayback] =
    createContext<ScorePlaybackContext>('MusicXmlPreview', 'ScorePlaybackContext');
